import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApplicationInsights,
  IDependencyTelemetry,
  IExceptionTelemetry,
  ITelemetryItem,
  SeverityLevel
} from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';

import { environment } from '../../../environments/environment';
import { ErrorHandler as AppErrorHandler } from '../errors/error-handler';
import { ErrorInfoModel } from '../models/service/error-info.model';
import { UserSessionService } from './user-session.service';

@Injectable({ providedIn: 'root' })
export class AppInsightsService {

  // Patterns we redact wherever they appear in telemetry text fields.
  // JWTs and emails are the highest-risk leaks from stack traces / error messages.
  private static readonly JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
  private static readonly EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

  private router = inject(Router);
  private userSessionService = inject(UserSessionService);

  private appInsights?: ApplicationInsights;

  constructor() {
    this.initialize();
    AppErrorHandler.registerAppInsightsTracker((error, errorInfo) => this.handleTrackedError(error, errorInfo));

    effect(() => {
      const user = this.userSessionService.activeUser();
      const org = this.userSessionService.activeOrganization();

      if (!this.appInsights) {
        return;
      }

      if (user) {
        this.appInsights.setAuthenticatedUserContext(
          user.userId.toString(),
          org?.organizationId.toString(),
          true
        );
      } else {
        this.appInsights.clearAuthenticatedUserContext();
      }
    });
  }

  public trackException(
    error: Error,
    severityLevel: SeverityLevel = SeverityLevel.Error,
    properties?: Record<string, unknown>
  ): void {
    if (!this.appInsights) {
      return;
    }

    const telemetry: IExceptionTelemetry = {
      exception: error,
      severityLevel,
      properties
    };

    this.appInsights.trackException(telemetry);
  }

  public trackEvent(name: string, properties?: Record<string, unknown>): void {
    this.appInsights?.trackEvent({ name, properties });
  }

  private initialize(): void {
    const connectionString = environment.applicationInsightsConnectionString;

    if (!connectionString) {
      if (environment.isDevelopment) {
        console.warn('Application Insights connection string is not configured; telemetry is disabled.');
      }
      return;
    }

    const angularPlugin = new AngularPlugin();

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router }
        },
        // Don't drop a cookie until we have a documented consent flow.
        disableCookiesUsage: true,
        // AngularPlugin handles route page-view tracking.
        enableAutoRouteTracking: false,
        // We want unhandled rejections to reach AI.
        enableUnhandledPromiseRejectionTracking: true,
        // 100% during rollout; tune down if volume becomes a problem.
        samplingPercentage: 100,
        // Don't send correlation headers to Auth0; their CORS preflight may reject them.
        correlationHeaderExcludedDomains: ['*.auth0.com', '*.membershine.com'],
        // Keep dependency telemetry on so we know which API call failed,
        // but the telemetry initializer below strips query strings + tokens.
        disableAjaxTracking: false,
        disableFetchTracking: false
      }
    });

    // Register the scrubber before loadAppInsights so it applies to the very first
    // telemetry items (initial page view, early unhandled rejections, etc.).
    this.appInsights.addTelemetryInitializer(item => this.scrubTelemetry(item));
    this.appInsights.loadAppInsights();
  }

  private handleTrackedError(error: Error, errorInfo?: ErrorInfoModel): void {
    const properties = errorInfo
      ? {
          redirect: errorInfo.redirect,
          swallow: errorInfo.swallow
        }
      : undefined;

    this.trackException(error, SeverityLevel.Error, properties);
  }

  private scrubTelemetry = (envelope: ITelemetryItem): boolean | void => {
    if (!envelope || !envelope.baseData) {
      return true;
    }

    const data = envelope.baseData;

    // Dependency telemetry (AJAX/Fetch): strip query strings + redact tokens from URLs.
    if (typeof (data as IDependencyTelemetry).target === 'string') {
      (data as IDependencyTelemetry).target = this.redact(this.stripQuery((data as IDependencyTelemetry).target as string));
    }
    if (typeof data['name'] === 'string') {
      data['name'] = this.redact(this.stripQuery(data['name']));
    }
    if (typeof data['data'] === 'string') {
      data['data'] = this.redact(this.stripQuery(data['data']));
    }

    // Page view telemetry: scrub URL.
    if (typeof data['uri'] === 'string') {
      data['uri'] = this.redact(this.stripQuery(data['uri']));
    }
    if (typeof data['refUri'] === 'string') {
      data['refUri'] = this.redact(this.stripQuery(data['refUri']));
    }

    // Exception telemetry: redact message and parsed stack frames.
    if (typeof data['message'] === 'string') {
      data['message'] = this.redact(data['message']);
    }
    if (Array.isArray(data['exceptions'])) {
      for (const ex of data['exceptions']) {
        if (typeof ex.message === 'string') {
          ex.message = this.redact(ex.message);
        }
        if (Array.isArray(ex.parsedStack)) {
          for (const frame of ex.parsedStack) {
            if (typeof frame.fileName === 'string') {
              frame.fileName = this.redact(this.stripQuery(frame.fileName));
            }
            if (typeof frame.method === 'string') {
              frame.method = this.redact(frame.method);
            }
          }
        }
      }
    }

    // Custom properties surface in two places depending on telemetry type:
    //   - envelope.data (top-level custom-properties bag)
    //   - envelope.baseData.properties (per-telemetry properties dict, used by trackException)
    // Redact both so a future caller passing tokens/emails via `properties` can't leak.
    this.redactStringValues(envelope.data);
    this.redactStringValues(data['properties']);

    return true;
  };

  private redactStringValues(bag: { [key: string]: unknown } | null | undefined): void {
    if (!bag) {
      return;
    }
    for (const key of Object.keys(bag)) {
      const value = bag[key];
      if (typeof value === 'string') {
        bag[key] = this.redact(value);
      }
    }
  }

  private stripQuery(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      return url.split('?')[0];
    }
  }

  private redact(text: string): string {
    return text
      .replace(AppInsightsService.JWT_PATTERN, '[REDACTED_JWT]')
      .replace(AppInsightsService.EMAIL_PATTERN, '[REDACTED_EMAIL]');
  }
}
